import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  Shield, Package, Users, Mail, Truck, Badge, RotateCcw, Activity, 
  LineChart, CloudUpload, BookOpen, Zap, FileText, Settings, 
  BarChart3, ChevronDown, ChevronRight, GripVertical, Gauge, Eye, Target, Sparkles
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { supabase } from "@/integrations/supabase/client";

// Categorized admin navigation items
const adminCategories = [
  {
    label: "Dashboard",
    items: [
      { title: "Overview", url: "/admin", icon: BarChart3 },
      { title: "Analytics", url: "/admin/analytics", icon: LineChart },
      { title: "Performance", url: "/admin/performance", icon: Gauge },
      { title: "Pixel Debugger", url: "/admin/pixel-debugger", icon: Target },
    ]
  },
  {
    label: "Content Management", 
    items: [
      { title: "Blog Posts", url: "/admin/blog-posts", icon: FileText },
      { title: "Knowledge Base", url: "/admin/knowledge-base", icon: BookOpen },
      { title: "Markdown Editor", url: "/admin/markdown-editor", icon: FileText },
      { title: "Homepage Images", url: "/admin/homepage-images", icon: Zap },
      { title: "Badges", url: "/admin/badges", icon: Badge },
    ]
  },
  {
    label: "E-commerce",
    items: [
      { title: "Suppliers", url: "/admin/suppliers", icon: Package },
      { title: "Orders", url: "/admin/orders", icon: Truck },
      { title: "Tech Inventory", url: "/admin/tech-inventory", icon: Settings },
      { title: "Shopify Enhancements", url: "/admin/shopify-enhancements", icon: Sparkles },
    ]
  },
  {
    label: "User Management",
    items: [
      { title: "Users", url: "/admin/users", icon: Users },
      { title: "Newsletter", url: "/admin/newsletter", icon: Mail },
      { title: "Voice Messages", url: "/admin/voice-messages", icon: Mail },
    ]
  },
  {
    label: "System",
    items: [
      { title: "Configuration", url: "/admin/config", icon: Settings },
      { title: "Security", url: "/admin/security", icon: Shield },
      { title: "Rate Limits", url: "/admin/rate-limits", icon: Shield },
      { title: "UX Testing", url: "/admin/ux-testing", icon: Eye },
      { title: "Backups", url: "/admin/backup", icon: CloudUpload },
      { title: "Restore Guide", url: "/admin/restore-guide", icon: RotateCcw },
    ]
  }
];

export function AdminSidebar() {
  // All hooks must be called before any conditional returns
  const { state } = useSidebar();
  const location = useLocation();
  const { isAdmin } = useUserRole();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // Load expanded state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('admin-sidebar-expanded');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setExpandedGroups(Array.isArray(parsed) ? parsed : [
          "Dashboard", "Content Management", "E-commerce", "User Management", "System"
        ]);
      } catch (error) {
        // Fallback to all expanded
        setExpandedGroups([
          "Dashboard", "Content Management", "E-commerce", "User Management", "System"
        ]);
      }
    } else {
      // Default to all expanded on first visit
      setExpandedGroups([
        "Dashboard", "Content Management", "E-commerce", "User Management", "System"
      ]);
    }
  }, []);

  // Early return after all hooks are called
  if (!isAdmin()) {
    return null;
  }

  const currentPath = location.pathname;
  const isActive = (path: string) => currentPath === path;
  const isCollapsed = state === "collapsed";

  // Save expanded state to localStorage
  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev => {
      const newState = prev.includes(groupLabel) 
        ? prev.filter(g => g !== groupLabel)
        : [...prev, groupLabel];
      
      localStorage.setItem('admin-sidebar-expanded', JSON.stringify(newState));
      return newState;
    });
  };

  // Find which group contains the active route to keep it expanded
  const activeGroup = adminCategories.find(category =>
    category.items.some(item => isActive(item.url))
  );

  return (
    <Sidebar
      className={cn(
        "border-r border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        isCollapsed ? "w-14" : "w-64"
      )}
      collapsible="icon"
    >
      {/* Sidebar Header */}
      <div className="flex items-center gap-2 p-4 border-b border-border/50">
        <Shield className="w-6 h-6 text-primary" />
        {!isCollapsed && (
          <div>
            <h2 className="font-semibold text-sm">Admin Panel</h2>
            <p className="text-xs text-muted-foreground">Management Console</p>
          </div>
        )}
      </div>

      <SidebarContent className="py-2">
        {adminCategories.map((category) => {
          const isGroupExpanded = expandedGroups.includes(category.label);
          
          return (
            <SidebarGroup key={category.label} className={cn("mb-0.5", !isGroupExpanded && "-mb-1")}>
              <Collapsible
                open={isGroupExpanded}
                onOpenChange={() => toggleGroup(category.label)}
              >
                <CollapsibleTrigger asChild>
                  <button className={cn(
                    "flex items-center justify-between w-full px-3 text-xs font-medium text-muted-foreground hover:text-foreground cursor-pointer rounded-md hover:bg-muted/30 transition-colors",
                    isGroupExpanded ? "py-1.5" : "py-0.5",
                    isCollapsed && "justify-center py-0.5 px-2"
                  )}>
                    {!isCollapsed && (
                      <>
                        <span>{category.label}</span>
                        {isGroupExpanded ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                      </>
                    )}
                    {isCollapsed && (
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                    )}
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu className="space-y-0.5">
                      {category.items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              end
                              className={({ isActive: linkIsActive }) => cn(
                                "flex items-center gap-3 px-3 py-2 text-sm transition-colors rounded-md",
                                linkIsActive
                                  ? "bg-primary/10 text-primary font-medium"
                                  : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                              )}
                            >
                              <item.icon className="w-4 h-4 flex-shrink-0" />
                              {!isCollapsed && <span className="truncate">{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      {/* Sidebar Footer */}
      {!isCollapsed && (
        <div className="mt-auto p-4 border-t border-border/50">
          <div className="text-xs text-muted-foreground">
            <div className="font-medium">Admin Dashboard</div>
            <div>v2.0.0 - Restructured</div>
          </div>
        </div>
      )}
    </Sidebar>
  );
}
